import type { VariantProps } from 'class-variance-authority';
import { cva } from 'class-variance-authority';

// The height is set as a variable because the addons and the control read it too.
export const inputVariants = cva('', {
    variants: {
        size: {
            sm: '[--input-height:var(--input-height-small)] [&>input]:text-sm [&>[data-slot=input-group-addon]]:text-sm',
            md: '',
            lg: '[--input-height:var(--button-height-lg)]',
        },
    },
    defaultVariants: {
        size: 'md',
    },
});

export type InputVariants = VariantProps<typeof inputVariants>;

// Poppins sets its letters high in the line, so the input inside the group is nudged down a pixel
// (`InputGroupInput`); the text in a slot takes the same nudge to stand on the same line. A button
// takes it back: it spans the field's full height.
//
// A button sits inside the field's box rather than in a box of its own: no shadow lifts it off the
// field, whatever its variant, and it is square except at the field's edge. There it lies over the
// field's border, so a variant with a border of its own shows one line rather than two, and takes the
// field's curve on that side. It is as tall as the field already, border included. Buttons side by
// side take back the slot's gap, and share a border the same way. Its focus ring is a box-shadow
// too, but one `shadow-none` leaves alone.
export const inputGroupAddonVariants = cva(
    'pt-px [&>[data-slot=button]]:-mt-px [&>[data-slot=button]]:rounded-none [&>[data-slot=button]]:shadow-none [&>[data-slot=button]+[data-slot=button]]:ml-[calc(var(--spacing)*-2-1px)] text-muted-foreground/80 flex h-full cursor-text items-center justify-center gap-2 text-base font-normal select-none [&>.sph-icon]:size-4.5! [&>kbd]:rounded-[calc(var(--radius)-5px)] group-data-[disabled=true]/input-group:opacity-50 group-data-[disabled=true]/input-group:cursor-not-allowed',
    {
        variants: {
            align: {
                'inline-start':
                    'order-first pl-3 has-[>button:first-child]:pl-0 has-[>kbd]:ml-[-0.35rem] [&>[data-slot=button]:first-child]:-ml-px [&>[data-slot=button]:first-child]:rounded-l-md',
                'inline-end':
                    'order-last pr-3 has-[>button:last-child]:pr-0 has-[>kbd]:mr-[-0.35rem] [&>[data-slot=button]:last-child]:-mr-px [&>[data-slot=button]:last-child]:rounded-r-md',
            },
        },
        defaultVariants: {
            align: 'inline-start',
        },
    },
);

export type InputGroupVariants = VariantProps<typeof inputGroupAddonVariants>;

export const inputGroupButtonVariants = cva(
    'text-sm shadow-none flex gap-2 items-center',
    {
        variants: {
            size: {
                xs: 'h-6 gap-1 px-2 rounded-[calc(var(--radius)-5px)] [&>svg:not([class*=\'size-\'])]:size-3.5 has-[>svg]:px-2',
                sm: 'h-8 px-2.5 gap-1.5 rounded-md has-[>svg]:px-2.5',
                'icon-xs': 'size-6 rounded-[calc(var(--radius)-5px)] p-0 has-[>svg]:p-0',
                'icon-sm': 'size-8 p-0 has-[>svg]:p-0',
            },
        },
        defaultVariants: {
            size: 'xs',
        },
    },
);

export type InputGroupButtonVariants = VariantProps<typeof inputGroupButtonVariants>;
